import React, { useState, type FC } from 'react'
import RecordTable from '../../components/records-table'
import { TRADER_INPUT_FIELDS_MAP } from '../../utility/constant'
import InputBox from '../../components/inputbox'
import ActionButton from '../../components/action-button'
import closeIcon from '../../utility/img/cancel.png'
import { RISK_BOND_EDITABLE_FIELDS } from './utils/constants'
import type { RiskAssetViewerProps } from './utils/types'
import { useRiskAssetDetails } from './hooks/useRiskAssetDetails'
import Message from '../../components/message'
import ConfirmationModal from '../../components/confirm-popup'
import ShowNotes from '../../components/show-notes'
import MiniNotepad from '../../components/notepad'
import { formatInputToDollar, getCurrencyPrefix } from '../../utility/formatting'
import ReconcileSummaryList from '../Cap-Trader/reconcile-components/ReconcileSummaryList'
import ReconcileDetails from '../Cap-Trader/reconcile-components/ReconcileDetails'

const RiskAssetViewer: FC<RiskAssetViewerProps> = ({
  show,
  onClose,
  batchId,
  batchUniqueId,
  setMessage,
  assetType,
  setMessageColor,
  updateError,
  status,
  basketOverall,
  fundingRequestId,
  fundingRequestCode,
  ttlNotional,
  ttlQuantity,
  currency,
  reconcileIndicator,
  reconcileCount,
  execScheme
}) => {
  const [isReconcilePopupOpen, setIsReconcilePopupOpen] = useState(false)
  const [selectedRecordIndex, setSelectedRecordIndex] = useState<number>(0)

  // Check if current status is a rejected status
  const isRejected = status === 'RISK-REJECTED' || status === 'TRADER-REJECTED'

  const {
    records,
    setRecords,
    inputData,
    setInputData,
    isMutating,
    isSecondaryMutating,
    loading,
    error,
    QueryWithExtendedTimeComponent,
    enableEdit,
    recalcMessage,
    recalcMessageColor,
    setRecalcMessage,
    dataChanged,
    noteChanged,
    handleCancel,
    handlePrimaryActionWithValidation,
    handleSecondaryClick,
    handleSave,
    preparingAsset,
    getPrimaryLabel,
    handleDiscard,
    isConfirmationDialogActive,
    setIsConfirmationDialogActive,
    handleSaveExit,
    handleCloseIconClick,
    riskNote,
    setRiskNote,
    displayingNotes,
    portfolioReadOnly,
    shouldShowRiskPreApprove,
    shouldShowRejectButton,
    reconciliationResultData,
    loadingReconciliation,
    errorReconciliation
  } = useRiskAssetDetails(
    {
      batchId,
      batchUniqueId,
      assetType,
      setMessage,
      setMessageColor,
      status,
      basketOverall,
      onClose,
      updateError,
      testDetails: true,
      fundingRequestId,
      reconcileIndicator,
      reconcileCount
    }
  )

  if (!show) return null

  // Handle all loading and error states in one place
  if (loading || (error ?? loadingReconciliation) || errorReconciliation) {
    if (loading || error) {
      return <QueryWithExtendedTimeComponent />
    }
    if (loadingReconciliation) {
      return (
        <div className="flex items-center justify-center p-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
            <p className="text-gray-600">Loading reconciliation data...</p>
          </div>
        </div>
      )
    }
    if (errorReconciliation) {
      return (
        <div className="flex items-center justify-center p-4">
          <div className="text-center text-red-600">
            <p className="font-semibold">Error loading reconciliation data</p>
            <p className="text-sm mt-1">{errorReconciliation.message}</p>
          </div>
        </div>
      )
    }
  }

  const approvedStatuses = new Set(['TRADER-APPROVED-EXCEPTION', 'TRADER-APPROVED', 'ASSET-READY', 'RISK-APPROVED', 'RISK-APPROVED-EXCEPTION'])
  const canRiskPreApprove = reconcileIndicator === 1 && portfolioReadOnly && (status === 'TRADER-PREAPPROVED' || status === 'TRADER-PREAPPROVED-EXCEPTION')
  const disableAllForTraderRejected = status === 'TRADER-REJECTED'
  const canRejectWhenCptyApproved = status === 'CPTY-APPROVED'
  const enableAction = !disableAllForTraderRejected && (approvedStatuses.has(status) || canRiskPreApprove || shouldShowRejectButton || shouldShowRiskPreApprove || canRejectWhenCptyApproved)
  const canShowRejectForAssetReady = status !== 'ASSET-READY'

  const handleOpenReconciliationPopup = () => {
    setIsReconcilePopupOpen(true)
  }

  const handleCloseReconciliationPopup = () => {
    setIsReconcilePopupOpen(false)
    setSelectedRecordIndex(0)
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-auto">
        {isConfirmationDialogActive && (
          <div className="fixed inset-0 bg-black bg-opacity-50"></div>
        )}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" aria-hidden="true"></div>
        <div className="relative bg-white rounded-lg shadow-xl transform transition-all sm:max-w-[70%] sm:my-8 w-auto max-h-[80%] min-w-[60%]">
          <div className='flex flex-row justify-between items-center px-4 pt-2'>
            <button onClick={handleCloseIconClick} className="transition duration-150 ease-in-out rounded-full p-2">
              <img src={closeIcon} alt='Close' width={24} height={24}/>
            </button>
            <div className='flex-grow mx-4 overflow-hidden flex justify-center items-center'>
              {recalcMessage && (
                <div className="overflow-auto whitespace-pre-wrap break-words">
                  <Message message={recalcMessage} color={recalcMessageColor} clearMessage={() => { setRecalcMessage('') }} />
                </div>
              )}
            </div>
            {!preparingAsset && enableEdit && (<button
              onClick={handleSave}
              className={`transition ease-in-out bg-green-600 hover:bg-green-700 text-white text-sm py-2 px-3 rounded-full ${(dataChanged || noteChanged) ? '' : 'opacity-50 cursor-not-allowed'}`}
              disabled={!(dataChanged || noteChanged)}
            >
              {isMutating
                ? (<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>)
                : 'Recalc'
              }
            </button>)}
          </div>

          <div className="flex flex-col" style={{ maxHeight: 'calc(70vh - 60px)' }}>
            <div className="flex-shrink-0">
              <InputBox
                fields={TRADER_INPUT_FIELDS_MAP(assetType)}
                inputData={inputData}
                setInputData={setInputData}
                isDataReady={false}
                readOnly={true}
              />
            </div>

            <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <div className="flex-1 min-h-0 overflow-auto px-4">
                <RecordTable
                  records={records}
                  setRecords={setRecords}
                  isDataReady={true}
                  editableFields={RISK_BOND_EDITABLE_FIELDS}
                  showEdit={enableEdit}
                  assetType={assetType}
                />
              </div>

              <div className='flex flex-col xl:flex-row space-y-2.5 xl:space-y-0 xl:space-x-2.5 p-4 pt-0'>
                <div className="flex-1 min-w-0 min-h-[100px]">
                  <ShowNotes data={displayingNotes} className='h-full'/>
                </div>
                <div className="flex-1 min-w-0 min-h-[100px]">
                  <MiniNotepad
                    noteText={riskNote}
                    setNoteText={setRiskNote}
                    disabled={!enableAction}
                    styleClass="h-full w-full mt-2"
                  />
                </div>
                <div className='flex flex-1 flex-col space-y-2.5 xl:space-y-0 xl:flex-row pt-2 min-w-fit'>
                  <div className="flex">
                    Notional MV:
                    <div className='ml-1 mr-2'>
                      <span className='bg-red-100 pl-2 pr-2'>{formatInputToDollar(ttlNotional ?? 0, { prefix: getCurrencyPrefix(currency) })}</span>
                    </div>
                  </div>
                  <div className="flex">
                    Total Quantity:
                    <div className='ml-1 mr-2'>
                      <span className='bg-red-100 pl-2 pr-2'>{formatInputToDollar(ttlQuantity ?? 0, { digit: 0 })}</span>
                    </div>
                  </div>
                  <div className="flex">
                    Num of assets:
                    <div className='ml-1'>
                      <span className='bg-yellow-100 pl-2 pr-2'>
                        {records.filter(record => !record.isRemoved).length}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center p-4">
              <div className="flex-1 flex space-x-4">
                {fundingRequestCode && (
                  <div className="flex items-center">
                    Inter-company Loan:
                    <div className="ml-1 pl-2 pr-2 bg-gray-100 rounded">
                      {fundingRequestCode}
                    </div>
                  </div>
                )}
                {!fundingRequestCode && execScheme && (
                  <div className="flex items-center">
                    Execution Scheme:
                    <div className="ml-1 pl-2 pr-2 bg-gray-100 rounded">
                      {execScheme}
                    </div>
                  </div>
                )}
                {fundingRequestCode && execScheme && (
                  <div className="flex items-center">
                    Execution Scheme:
                    <div className="ml-1 pl-2 pr-2 bg-gray-100 rounded">
                      {execScheme}
                    </div>
                  </div>
                )}
              </div>

              {reconcileIndicator === 1 && reconcileCount > 0 && (
                <button
                  onClick={handleOpenReconciliationPopup}
                  className="text-red-500 underline mr-4"
                >
                  Reconcile Details: {reconcileCount}
                </button>
              )}
              {(enableAction ?? enableEdit) && (
                <div className="flex flex-shrink-0 justify-end">
                  {status === 'CPTY-APPROVED'
                    ? dataChanged
                      ? (
                        <ActionButton
                          primaryLabel="Save"
                          secondaryLabel="Cancel"
                          primaryAction={handleSave}
                          secondaryAction={handleCancel}
                          primaryDisabled={false}
                          primaryLoading={isMutating}
                          secondaryLoading={false}
                        />
                        )
                      : (
                        <ActionButton
                          primaryLabel="Reject"
                          secondaryLabel="Close"
                          primaryAction={handleSecondaryClick}
                          secondaryAction={handleCancel}
                          primaryLoading={isSecondaryMutating}
                          secondaryLoading={false}
                          primaryDisabled={false}
                          primaryButtonColor="bg-red-600 hover:bg-red-700 text-white"
                        />
                        )
                    : shouldShowRejectButton && canShowRejectForAssetReady
                      ? (
                        // Show only reject button for RISK-PREAPPROVED states
                        <ActionButton
                          primaryLabel="Reject"
                          secondaryLabel="Close"
                          primaryAction={handleSecondaryClick}
                          secondaryAction={handleCancel}
                          primaryLoading={isSecondaryMutating}
                          secondaryLoading={false}
                          primaryDisabled={false}
                          primaryButtonColor="bg-red-600 hover:bg-red-700 text-white"
                        />
                        )
                      : (status === 'RISK-APPROVED' || status === 'RISK-APPROVED-EXCEPTION') && canShowRejectForAssetReady
                          ? (
                        <ActionButton
                          primaryLabel="Reject"
                          secondaryLabel="Close"
                          primaryAction={handleSecondaryClick}
                          secondaryAction={handleCancel}
                          primaryLoading={isSecondaryMutating}
                          secondaryLoading={false}
                          primaryButtonColor="bg-red-600 hover:bg-red-700 text-white"
                        />
                            )
                          : (
                        <ActionButton
                          primaryLabel={getPrimaryLabel}
                          secondaryLabel={isRejected || !canShowRejectForAssetReady ? 'Close' : 'Reject'}
                          primaryAction={handlePrimaryActionWithValidation}
                          secondaryAction={isRejected || !canShowRejectForAssetReady ? handleCancel : handleSecondaryClick}
                          primaryLoading={isMutating}
                          secondaryLoading={isSecondaryMutating}
                          primaryDisabled={disableAllForTraderRejected || (preparingAsset ? !(dataChanged || noteChanged) : dataChanged)}
                          secondaryButtonColor={isRejected || !canShowRejectForAssetReady ? undefined : 'bg-red-600 hover:bg-red-700 text-white'}
                        />
                            )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    {isReconcilePopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="overflow-auto bg-white rounded-lg shadow-lg w-3/4 h-3/4 p-4 relative max-h-fit">
            <div className="flex justify-between p-4">
              <button onClick={handleCloseReconciliationPopup} className="transition duration-150 ease-in-out rounded-full">
                <img src={closeIcon} alt="Close" width={24} height={24} />
              </button>
              <h2 className="text-xl font-bold text-justify">Reconciliation Details</h2>
              <ReconcileSummaryList
                resultData={reconciliationResultData?.summaryData ?? []}
                setSelectedRecordIndex={setSelectedRecordIndex}
                selectedRecordIndex={selectedRecordIndex}
              />
            </div>
            <ReconcileDetails
              summaryData={reconciliationResultData?.summaryData[selectedRecordIndex]}
              detailsData={reconciliationResultData?.detailsData}
            />
          </div>
        </div>
    )}

    <ConfirmationModal
      isOpen={isConfirmationDialogActive}
      onClose={handleDiscard}
      onConfirm={handleSaveExit}
      onCancel={async () => { setIsConfirmationDialogActive(false) }}
      question="You have some unsaved changes, do you want to save them?"
      title="Changes made"
      confirmText="Save"
      cancelText="Discard"
      primaryLoading={isMutating}
      secondaryLoading={isSecondaryMutating}
    />
    </>
  )
}

export default RiskAssetViewer
